import { skipToken } from '@reduxjs/toolkit/query';
import { ReactNode, createContext, useContext, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetPinQuery, useResetPinMutation } from 'services/backend';
import { RootState } from 'store/index';

import { PinModal } from 'components/Pin/PinModal';
import { ResetPinConfirmModal } from 'components/Pin/ResetPinConfirmModal';
import { ResetPinMessageModal } from 'components/Pin/ResetPinMessageModal';

type PinContextType = {
  requirePin: () => Promise<boolean>;
  openResetPin: () => void;
  closePinModal: () => void;
  isPinModalVisible: boolean;
  hasPin: boolean;
  isGetPinQueryLoading: boolean;
};

const PinContext = createContext<PinContextType | undefined>(undefined);

export const PinProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const {
    data: pinData,
    isLoading: isGetPinQueryLoading,
    refetch,
  } = useGetPinQuery(isAuthenticated ? undefined : skipToken, {
    refetchOnMountOrArgChange: true,
  });
  const [resetPin, { isLoading: isResetPinLoading }] = useResetPinMutation();

  const [isPinModalVisible, setPinModalVisible] = useState(false);
  const [isResetConfirmVisible, setResetConfirmVisible] = useState(false);
  const [resetMessageType, setResetMessageType] = useState<'success' | 'error'>('success');
  const [isResetMessageVisible, setResetMessageVisible] = useState(false);
  const [error, setError] = useState(false);
  const resolverRef = useRef<(value: boolean) => void>();

  const hasPin = !!pinData;

  const openResetPin = () => setResetConfirmVisible(true);

  const closePinModal = () => setPinModalVisible(false);

  const requirePin = async () => {
    await refetch();

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setError(false);
      setPinModalVisible(true);
    });
  };

  const handleConfirm = (pin: string) => {
    const isValid = pin === pinData?.pin;

    if (!isValid) {
      setError(true);

      setTimeout(() => {
        setError(false);
      }, 1000);

      return;
    }

    resolverRef.current?.(true);
    setPinModalVisible(false);
  };

  const handleResetConfirm = async () => {
    try {
      await resetPin().unwrap();

      setResetConfirmVisible(false);
      setResetMessageType('success');
      setResetMessageVisible(true);
    } catch (err) {
      console.error('Reset PIN error', err);

      setResetConfirmVisible(false);
      setResetMessageType('error');
      setResetMessageVisible(true);
    }
  };

  const handleCancel = () => {
    resolverRef.current?.(false);
    setPinModalVisible(false);
  };

  return (
    <PinContext.Provider
      value={{
        requirePin,
        openResetPin,
        closePinModal,
        isPinModalVisible,
        hasPin,
        isGetPinQueryLoading,
      }}>
      {children}
      <PinModal
        isOpen={isPinModalVisible}
        error={error}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <ResetPinConfirmModal
        isOpen={isResetConfirmVisible}
        onConfirm={handleResetConfirm}
        onCancel={() => setResetConfirmVisible(false)}
        isLoading={isResetPinLoading}
      />

      <ResetPinMessageModal
        isOpen={isResetMessageVisible}
        type={resetMessageType}
        onClose={() => setResetMessageVisible(false)}
      />
    </PinContext.Provider>
  );
};

export const usePin = (): PinContextType => {
  const context = useContext(PinContext);

  if (!context) {
    throw new Error('usePin must be used within PinProvider');
  }

  return context;
};
